import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { locationApi } from '../services/locationApi';
import type { LocationFormData } from '../types/boat.types';

export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...locationKeys.lists(), filters] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...locationKeys.details(), id] as const,
};

export function useLocations(filters?: Record<string, any>) {
  return useQuery({
    queryKey: locationKeys.list(filters || {}),
    queryFn: () => locationApi.getLocations(filters),
  });
}

export function useLocation(id: number | string) {
  return useQuery({
    queryKey: locationKeys.detail(id),
    queryFn: () => locationApi.getLocation(id),
    enabled: !!id,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LocationFormData) => locationApi.createLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: Partial<LocationFormData> }) =>
      locationApi.updateLocation(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => locationApi.deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
    },
  });
}
